import { useState, useEffect, useMemo } from 'react';
import { formatCurrency } from '../../views/component/CarCard';
import SellVehicleModal from './SellVehicleModal';
import Car, { type CarInvestor } from '../../models/Car';
import EditableField from '../../views/component/EditableField';
import { InvestorLedgerViewModel } from '@lumora/core';
import { client } from '../../client';
import { carFromListing, saveCar, stock } from '../../lib/stock';
import { prepareUploadBlob, uploadErrorMessage } from './imageResize';
import AppointmentRequestModal from '../../views/component/AppointmentRequestModal';

interface CarDetailsModalProps {
    car: Car;
    onClose: () => void;
    isAdmin: boolean;
    isStaff: boolean;
    canEditFinancials: boolean;
    isAdminEditMode: boolean;
    refreshInventory: () => void | Promise<void>;
}

export default function CarDetailsModal({ 
    car: initialCar, 
    onClose, 
    isAdmin, 
    isStaff,
    canEditFinancials,
    isAdminEditMode, 
    refreshInventory 
}: CarDetailsModalProps) {
    const [car, setCar] = useState<Car>(initialCar);
    const [imageIndex, setImageIndex] = useState<number>(0);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string>('');
    const [isSelling, setIsSelling] = useState<boolean>(false);
    const [isQuickScheduling, setIsQuickScheduling] = useState<boolean>(false);
    const investorsVm = useMemo(() => new InvestorLedgerViewModel(client), []);
    const [availableInvestors, setAvailableInvestors] = useState<{id: string, name: string}[]>([]);

    useEffect(() => {
        setCar(initialCar);
    }, [initialCar]);

    useEffect(() => {
        if (isAdminEditMode) {
            investorsVm.load()
                .then(() => setAvailableInvestors(investorsVm.state.investors.map(i => ({ id: i.id, name: i.name }))))
                .catch(console.error);
        }
    }, [isAdminEditMode, investorsVm]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && !isSelling) onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose, isSelling]);

    if (!car) return null;

    const images: string[] = car.images && car.images.length > 0 ? car.images : [];
    const mainImage: string = images.length > 0 ? images[imageIndex] : 'https://placehold.co/800x600/cccccc/ffffff?text=No+Image';

    const handleUpdateCar = async (updatedCar: Car | Partial<Car>) => {
        const carInstance = updatedCar instanceof Car ? updatedCar : new Car({ ...car, ...updatedCar });
        setCar(carInstance);
        
        const saved = await saveCar(carInstance);
        if (saved) {
            if (refreshInventory) refreshInventory();
        } else {
            console.error('Failed to auto-save car details:', stock.state.error);
        }
    };

    const handleShare = (): void => {
        const shareText: string = `Check out this ${car.year} ${car.make} ${car.model} at Al Amir Auto!\n${window.location.origin}/?carId=${car.id}`;
        const textArea = document.createElement("textarea");
        textArea.value = shareText;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            console.log('Link copied to clipboard!');
        } catch (err) {
            console.error('Share failed', err);
        }
        document.body.removeChild(textArea);
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        if (!e.target.files || e.target.files.length === 0) return;
        setIsUploading(true);
        setUploadError('');
        const input = e.target;
        try {
            const photos: { blob: Blob; name: string }[] = [];
            for (let i = 0; i < input.files!.length; i++) {
                const file = input.files![i];
                const blob = await prepareUploadBlob(file, 1440);
                photos.push({ blob, name: file.name });
            }
            const updated = await stock.addPhotos(car.id, photos);
            if (updated) {
                setCar(carFromListing(updated));
                if (refreshInventory) await refreshInventory();
            }
        } catch (err) {
            console.error(err);
            setUploadError(uploadErrorMessage(err));
        } finally {
            setIsUploading(false);
            input.value = '';
        }
    };

    const moveImage = (idx: number, direction: 'left' | 'right') => {
        const newImages = [...images];
        if (direction === 'left' && idx > 0) {
            [newImages[idx - 1], newImages[idx]] = [newImages[idx], newImages[idx - 1]];
        } else if (direction === 'right' && idx < newImages.length - 1) {
            [newImages[idx + 1], newImages[idx]] = [newImages[idx], newImages[idx + 1]];
        } else {
            return;
        }
        
        if (imageIndex === idx) {
            setImageIndex(direction === 'left' ? idx - 1 : idx + 1);
        } else if (direction === 'left' && imageIndex === idx - 1) {
            setImageIndex(idx);
        } else if (direction === 'right' && imageIndex === idx + 1) {
            setImageIndex(idx);
        }

        handleUpdateCar({ images: newImages });
    };

    const removeImage = (idx: number) => {
        if (!window.confirm("Remove this image?")) return;
        const newImages = [...images];
        newImages.splice(idx, 1);
        
        if (imageIndex >= newImages.length) {
            setImageIndex(Math.max(0, newImages.length - 1));
        } else if (imageIndex > idx) {
            setImageIndex(imageIndex - 1);
        }
        
        handleUpdateCar({ images: newImages });
    };

    const onSaleSuccess = (): void => { if (refreshInventory) refreshInventory(); onClose(); };

    const handleDelete = async (): Promise<void> => {
        if (!window.confirm(`Are you sure you want to delete this ${car.year} ${car.make} ${car.model}?`)) return;
        if (await stock.archive(car.id)) {
            if (refreshInventory) await refreshInventory();
            onClose();
        } else {
            console.error('Error deleting listing:', stock.state.error);
        }
    };

    const addInvestorToCar = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = e.target.value;
        if(!id) return;
        const investor = availableInvestors.find(i => i.id === id);
        if(!investor || car.investors.some(i => i.id === id)) return;
        
        handleUpdateCar({ investors: [...car.investors, { id: investor.id, name: investor.name, amount: 0, percentage: 0 }] });
        e.target.value = "";
    };

    const updateCarInvestor = (idx: number, field: keyof CarInvestor, value: number) => {
        const updated = [...car.investors];
        updated[idx] = { ...updated[idx], [field]: value };
        handleUpdateCar({ investors: updated });
    };

    const removeCarInvestor = (idx: number) => {
        const updated = [...car.investors];
        updated.splice(idx, 1);
        handleUpdateCar({ investors: updated });
    };

    const totalInvested = car.investors.reduce((sum, i) => sum + i.amount, 0);
    const totalPercentage = car.investors.reduce((sum, i) => sum + i.percentage, 0);

  return (
        <>
            <div 
                className="fixed inset-0 bg-black/90 z-[60] flex items-start sm:items-center justify-center p-0 sm:p-4 backdrop-blur-md overflow-y-auto"
                onClick={() => onClose()}
            >
                <div className="fixed top-4 right-4 z-[100] flex space-x-2" onClick={(e) => e.stopPropagation()}>
                    <button 
                        onClick={handleShare} 
                        className="p-3 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-full shadow-2xl transition text-white hover:text-red-400"
                        title="Share Vehicle"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                    </button>
                    <button 
                        onClick={() => onClose()} 
                        className="p-3 bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/20 rounded-full shadow-2xl transition text-white hover:text-red-400"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                <div 
                    className="bg-white rounded-none sm:rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col md:flex-row overflow-hidden relative animate-in fade-in zoom-in duration-300 my-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    
                    {/* Left Side: Images */}
                    <div className="w-full md:w-3/5 bg-gray-100 flex flex-col h-[50vh] md:h-auto min-h-[300px] border-b md:border-b-0 md:border-r border-gray-200">
                        <div className="relative flex-grow flex items-center justify-center p-6">
                            <img src={mainImage} alt={car.model} className="max-h-full max-w-full object-contain drop-shadow-2xl select-none" />
                            
                            {images.length > 1 && (
                                <>
                                    <button 
                                        onClick={() => setImageIndex((prev) => (prev - 1 + images.length) % images.length)} 
                                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white text-gray-800 w-10 h-10 flex items-center justify-center rounded-full transition md:left-4"
                                    >
                                        &#10094;
                                    </button>
                                    <button 
                                        onClick={() => setImageIndex((prev) => (prev + 1) % images.length)} 
                                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white text-gray-800 w-10 h-10 flex items-center justify-center rounded-full transition md:right-4"
                                    >
                                        &#10095;
                                    </button>
                                </>
                            )}
                        </div>
                        {/* Thumbnails */}
                        {images.length > 0 && (
                            <div className="h-20 p-2 flex space-x-2 overflow-x-auto bg-gray-200/50 scrollbar-hide">
                                {images.map((img: string, idx: number) => (
                                    <div key={idx} className="relative group flex-shrink-0 h-full aspect-video">
                                        <img 
                                            src={img} 
                                            onClick={() => setImageIndex(idx)}
                                            className={`w-full h-full object-cover rounded cursor-pointer border-2 transition ${idx === imageIndex ? 'border-red-600' : 'border-transparent opacity-50'}`}
                                        />
                                        {isAdminEditMode && (
                                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center space-x-2 transition rounded backdrop-blur-sm">
                                                <button onClick={(e) => { e.stopPropagation(); moveImage(idx, 'left'); }} disabled={idx === 0} className="text-white hover:text-blue-400 p-1 disabled:opacity-20">&#9664;</button>
                                                <button onClick={(e) => { e.stopPropagation(); removeImage(idx); }} className="text-white hover:text-red-500 p-1">&#10006;</button>
                                                <button onClick={(e) => { e.stopPropagation(); moveImage(idx, 'right'); }} disabled={idx === images.length - 1} className="text-white hover:text-blue-400 p-1 disabled:opacity-20">&#9654;</button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Right Side: Details */}
                    <div className="w-full md:w-2/5 p-6 md:p-10 flex flex-col bg-white overflow-y-auto max-h-[90vh] custom-scrollbar">
                        <div className="mb-4 pr-12 md:pr-0">
                            <div className="flex flex-wrap gap-x-2">
                                <EditableField 
                                    contentKey="year" content={car} setContent={handleUpdateCar} isEditMode={isAdminEditMode} as="h2" 
                                    className="text-2xl sm:text-3xl font-black italic uppercase tracking-tighter text-gray-900 leading-tight" 
                                />
                                <EditableField 
                                    contentKey="make" content={car} setContent={handleUpdateCar} isEditMode={isAdminEditMode} as="h2" 
                                    className="text-2xl sm:text-3xl font-black italic uppercase tracking-tighter text-gray-900 leading-tight" 
                                />
                            </div>
                            <EditableField 
                                contentKey="model" content={car} setContent={handleUpdateCar} isEditMode={isAdminEditMode} as="p" 
                                className="text-lg font-bold text-red-600 uppercase tracking-widest mt-1" 
                            />
                        </div>

                        <div className="text-3xl sm:text-4xl font-black text-gray-900 mb-6 border-b-4 border-red-600 inline-block pb-2 w-fit">
                            {isAdminEditMode ? (
                                <div className="flex items-center space-x-1">
                                    <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">Asking: $</span>
                                    <EditableField contentKey="price" content={car} setContent={handleUpdateCar} isEditMode={isAdminEditMode} className="w-32" />
                                </div>
                            ) : formatCurrency(car.price)}
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="bg-gray-50 p-3 rounded-xl">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Mileage</span>
                                <div className="flex items-center space-x-1 font-bold text-gray-800">
                                    <EditableField contentKey="mileage" content={car} setContent={handleUpdateCar} isEditMode={isAdminEditMode} />
                                    {!isAdminEditMode && <span>mi</span>}
                                </div>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-xl">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Color</span>
                                <div className="font-bold text-gray-800">
                                    <EditableField contentKey="color" content={car} setContent={handleUpdateCar} isEditMode={isAdminEditMode} />
                                </div>
                            </div>
                        </div>

                        <div className="mb-8">
                            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Description</h4>
                            <div className="text-gray-700 text-sm leading-relaxed">
                                <EditableField contentKey="description" content={car} setContent={handleUpdateCar} useHtml={true} isEditMode={isAdminEditMode} as="div" />
                            </div>
                        </div>

                        {/* Admin Financials / Ownership Section */}
                        {isAdminEditMode && canEditFinancials && (
                            <div className="mb-8 p-5 bg-gray-50 border border-gray-200 rounded-2xl">
                                <h4 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-4 border-b pb-2">Financials & Investors</h4>
                                
                                <div className="flex items-center space-x-2 mb-6">
                                    <span className="text-xs font-bold text-gray-600 uppercase tracking-widest">Purchase Cost: $</span>
                                    <EditableField contentKey="purchase_cost" content={car} setContent={handleUpdateCar} isEditMode={isAdminEditMode} className="w-32 p-1" />
                                </div>

                                <div className="space-y-3 mb-4">
                                    <div className="flex justify-between text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                        <span>Owner</span>
                                        <span>Capital</span>
                                        <span>Profit Share</span>
                                    </div>
                                    
                                    {/* Al Amir Implicit Share */}
                                    <div className="flex items-center justify-between text-xs font-bold bg-white p-2 rounded border border-gray-100">
                                        <span className="text-red-600">Al Amir Auto</span>
                                        <span>${Math.max(0, car.purchase_cost - totalInvested)}</span>
                                        <span>{Math.max(0, 100 - totalPercentage)}%</span>
                                    </div>

                                    {/* Additional Investors */}
                                    {car.investors.map((inv, idx) => (
                                        <div key={idx} className="flex items-center justify-between text-xs font-bold bg-white p-2 rounded border border-gray-100 group">
                                            <span className="flex items-center space-x-2">
                                                <button onClick={() => removeCarInvestor(idx)} className="text-red-400 hover:text-red-600">&times;</button>
                                                <span>{inv.name}</span>
                                            </span>
                                            <div className="flex items-center space-x-1">
                                                <span>$</span>
                                                <input 
                                                    type="number" value={inv.amount || 0} 
                                                    onChange={e => updateCarInvestor(idx, 'amount', Number(e.target.value))}
                                                    className="w-20 border rounded px-1 outline-none focus:border-red-500"
                                                />
                                            </div>
                                            <div className="flex items-center space-x-1">
                                                <input 
                                                    type="number" value={inv.percentage || 0} 
                                                    onChange={e => updateCarInvestor(idx, 'percentage', Number(e.target.value))}
                                                    className="w-16 border rounded px-1 outline-none focus:border-red-500 text-right"
                                                />
                                                <span>%</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <select 
                                    onChange={addInvestorToCar}
                                    className="w-full text-xs font-bold text-gray-600 uppercase tracking-widest border border-gray-200 rounded p-2 outline-none"
                                >
                                    <option value="">+ Add Co-Investor...</option>
                                    {availableInvestors.filter(ai => !car.investors.some(ci => ci.id === ai.id)).map(inv => (
                                        <option key={inv.id} value={inv.id}>{inv.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="mt-auto space-y-3 pb-6 md:pb-0">
                            {(isAdmin || isAdminEditMode) && (
                            <div className="flex w-full gap-4">
                                {(isAdmin || isAdminEditMode) && (
                                    <button onClick={() => setIsSelling(true)} className="flex-1 bg-green-600 text-white font-black uppercase py-4 rounded-xl hover:bg-green-700 transition flex items-center justify-center space-x-2">
                                        <span>Finalize Sale</span>
                                    </button>
                                )}
                                
                                {isAdminEditMode && isAdmin && (
                                    <button onClick={handleDelete} className="flex-1 bg-red-600 text-white font-black uppercase py-4 rounded-xl hover:bg-red-700 transition flex items-center justify-center space-x-2">
                                        <span>Delete Listing</span>
                                    </button>
                                )}
                            </div>
                            )}

                            <a href="tel:5078505048" className="w-full bg-red-600 text-white font-black uppercase py-4 rounded-xl hover:bg-red-700 transition flex items-center justify-center mt-4">
                                Call For Price
                            </a>
                            {isStaff && (
                                <button
                                    type="button"
                                    onClick={() => setIsQuickScheduling(true)}
                                    className="w-full bg-gray-900 text-white font-black uppercase py-4 rounded-xl hover:bg-gray-800 transition flex items-center justify-center"
                                >
                                    Quick Schedule Showing
                                </button>
                            )}
                            {isAdminEditMode && (
                                <div className="pt-4 border-t border-gray-100 mt-4">
                                    <label className="block text-[10px] font-black text-amber-600 uppercase tracking-widest mb-2">Staff: Add Listing Photos</label>
                                    <input 
                                        type="file" 
                                        multiple 
                                        accept="image/*"
                                        onChange={handleImageUpload} 
                                        disabled={isUploading} 
                                        className="block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-[10px] file:font-black file:uppercase file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100 cursor-pointer" 
                                    />
                                    {isUploading && <p className="text-[10px] font-bold text-blue-600 mt-2 animate-pulse uppercase">Uploading images...</p>}
                                    {uploadError && <p role="alert" className="text-xs font-bold text-red-600 mt-2">{uploadError}</p>}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {isSelling && (
                <SellVehicleModal car={car} onClose={() => setIsSelling(false)} onSaleSuccess={onSaleSuccess} canFinalizeSale={canEditFinancials} />
            )}
            {isQuickScheduling && (
                <AppointmentRequestModal
                    car={car}
                    mode="associate-schedule"
                    onClose={() => setIsQuickScheduling(false)}
                />
            )}
        </>
    );
}
